import { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase';
import {
  collection, getDocs, doc, setDoc, deleteDoc, serverTimestamp, query, orderBy, limit,
} from 'firebase/firestore';
import { hasAccess } from '../../lib/modules';

const CustomerManagementView = ({ onBack, currentUser }) => {
  // === permisos para la vista "customers" ===
  const canRead   = hasAccess(currentUser, 'customers', 'Lectura');
  const canEdit   = hasAccess(currentUser, 'customers', 'Edición');
  const canDelete = hasAccess(currentUser, 'customers', 'Eliminación');

  // Si no puede leer, no renderizamos la vista
  if (!canRead) {
    return (
      <div className="p-6">
        <p className="text-red-600">No tienes permisos para ver Clientes.</p>
        <button className="mt-4 px-4 py-2 rounded bg-gray-200" onClick={onBack}>Volver</button>
      </div>
    );
  }

  const [customers, setCustomers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [selectedCustomerHistory, setSelectedCustomerHistory] = useState(null);
  const [currentSubView, setCurrentSubView] = useState('list'); // 'list' | 'bestCustomers'
  const [historyFilters, setHistoryFilters] = useState({ startDate: '', endDate: '' });
  const [filter, setFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 30;

  useEffect(() => {
    let isActive = true;

    const normalizeDate = (value) => {
      if (!value) return '';
      if (typeof value === 'string') return value;
      if (value?.toDate) return value.toDate().toISOString();
      return '';
    };

    (async () => {
      try {
        const [customerSnap, salesSnap] = await Promise.all([
          getDocs(query(collection(db, 'clientes'), limit(500))),
          getDocs(query(collection(db, 'ventas'), orderBy('date', 'desc'), limit(500))),
        ]);

        const purchasesByCustomer = {};

        const buildPurchaseEntry = (saleDoc) => {
          const dateValue = normalizeDate(saleDoc.date) || normalizeDate(saleDoc.createdAt);
          return {
            saleId: saleDoc.saleId || saleDoc.id,
            date: dateValue || '',
            total: Number(saleDoc.total || 0),
            products: (saleDoc.products || []).map(prod => ({
              ...prod,
              qty: prod.qty ?? prod.quantity ?? 0,
            })),
          };
        };

        salesSnap.docs.forEach((saleSnap) => {
          const saleData = { id: saleSnap.id, ...saleSnap.data() };
          const customerKey = saleData.customerId;
          if (!customerKey) return;
          const purchaseEntry = buildPurchaseEntry(saleData);
          purchasesByCustomer[customerKey] = purchasesByCustomer[customerKey] || [];
          purchasesByCustomer[customerKey].push(purchaseEntry);
        });

        Object.values(purchasesByCustomer).forEach(arr => {
          arr.sort((a, b) => {
            if (!a.date) return 1;
            if (!b.date) return -1;
            return b.date.localeCompare(a.date);
          });
        });

        const cloud = customerSnap.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data(),
          purchaseHistory: purchasesByCustomer[docSnap.id] || [],
        }));

        if (isActive) {
          setCustomers(cloud);
        }
      } catch (e) {
        console.error('No se pudo leer Firestore, sigo con locales', e);
      }
    })();

    return () => {
      isActive = false;
    };
  }, []);

  const filteredCustomers = useMemo(() => {
    const normalize = (value) => String(value || '').toLowerCase().replace(/\s|[.\-]/g, '');
    const normFilter = normalize(filter);
    return customers.filter(c =>
      !normFilter ||
      normalize(c.cedula).includes(normFilter) ||
      normalize(c.name).includes(normFilter)
    );
  }, [customers, filter]);

  const totalPages = Math.max(1, Math.ceil(filteredCustomers.length / ITEMS_PER_PAGE));
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedCustomers = filteredCustomers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // === Create/Update ===
  const handleSaveCustomer = async (customerData) => {
    if (!canEdit) {
      alert('No tienes permiso para crear/editar clientes.');
      return;
    }

    const data = { ...customerData };

    // Validación: cédula obligatoria
    if (!data.cedula || String(data.cedula).trim() === '') {
      alert('La cédula es obligatoria.');
      return;
    }

    // Normaliza cédula
    data.cedula = String(data.cedula).replace(/\D/g, '');

    // Validación: cédula única (excepto el mismo cliente en edición)
    const exists = customers.some(c => c.cedula === data.cedula && c.id !== data.id);
    if (exists) {
      alert('Ya existe un cliente con esa cédula.');
      return;
    }

    // Actualiza lista local
    if (editingCustomer) {
      setCustomers(prev => prev.map(cust => (
        cust.id === data.id ? { ...cust, ...data, purchaseHistory: cust.purchaseHistory || [] } : cust
      )));
    } else {
      setCustomers(prev => [...prev, { ...data, id: data.cedula, purchaseHistory: [] }]);
    }

    // Persiste
    try {
      await setDoc(
        doc(db, 'clientes', data.cedula),           // ID = cédula
        { ...data, updatedAt: serverTimestamp() },
        { merge: true }
      );
    } catch (e) {
      console.error('No se pudo guardar en Firestore', e);
    }

    setEditingCustomer(null);
    setShowForm(false);
  };

  // === Delete ===
  const handleDeleteCustomer = async (id) => {
    if (!canDelete) {
      alert('No tienes permiso para eliminar clientes.');
      return;
    }
    if (!window.confirm('¿Estás seguro de que quieres eliminar a este cliente?')) return;

    const cust = customers.find(c => c.id === id);
    if (cust?.cedula) {
      try {
        await deleteDoc(doc(db, 'clientes', String(cust.cedula)));
      } catch (e) {
        console.error('No se pudo borrar en Firestore', e);
      }
    }

    setCustomers(prev => prev.filter(cust => cust.id !== id));
  };

  // === Edit ===
  const handleEditCustomer = (customer) => {
    if (!canEdit) return; // doble cerrojo
    setEditingCustomer({ ...customer });
    setShowForm(true);
  };

  const handleViewPurchaseHistory = (customer) => {
    setSelectedCustomerHistory(customer);
  };

  const getBestCustomers = () => {
    const safeHist = (arr) => Array.isArray(arr) ? arr : [];
    const customerTotals = customers.map(customer => ({
      ...customer,
      totalSpent: safeHist(customer.purchaseHistory).reduce((sum, p) => sum + (p.total || 0), 0),
    }));
    return customerTotals.sort((a, b) => b.totalSpent - a.totalSpent);
  };

  const renderSubView = () => {
    switch (currentSubView) {
      case 'list': {
        const displayStart = filteredCustomers.length === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;
        const displayEnd = Math.min(filteredCustomers.length, currentPage * ITEMS_PER_PAGE);

        return (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              {/* Buscador */}
              <div className="p-4 bg-white rounded-xl shadow mb-3">
                <input
                  type="text"
                  placeholder="Buscar por CC o nombre..."
                  value={filter}
                  onChange={(e) => {
                    setFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CC</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Teléfono</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Comprado</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedCustomers.length > 0 ? (
                    paginatedCustomers.map((customer) => (
                      <tr key={customer.id} className="hover:bg-gray-50 transition duration-150">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{customer.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{customer.cedula}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{customer.email || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{customer.phone || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          ${((customer.purchaseHistory || []).reduce((s, p) => s + (p.total || 0), 0)).toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleEditCustomer(customer)}
                            disabled={!canEdit}
                            className={`mr-3 ${!canEdit ? 'opacity-50 cursor-not-allowed' : 'text-blue-600 hover:text-blue-800'}`}
                          >
                            Editar
                          </button>

                          {canDelete && (
                            <button
                              onClick={() => handleDeleteCustomer(customer.id)}
                              className="text-red-600 hover:text-red-800 mr-3"
                            >
                              Eliminar
                            </button>
                          )}

                          <button
                            onClick={() => handleViewPurchaseHistory(customer)}
                            className="text-green-600 hover:text-green-800"
                          >
                            Historial
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                        {customers.length === 0 ? 'No hay clientes registrados.' : 'No hay resultados con ese filtro.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between text-sm text-gray-600">
                <span>
                  Mostrando {displayStart} - {displayEnd} de {filteredCustomers.length}
                </span>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded border ${currentPage === 1 ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 border-gray-300 hover:border-gray-400'}`}
                  >
                    Anterior
                  </button>
                  <span>
                    Página {currentPage} de {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1 rounded border ${currentPage === totalPages ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 border-gray-300 hover:border-gray-400'}`}
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      }

      case 'bestCustomers': {
        const bestCustomers = getBestCustomers();
        return (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden p-6">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Mejores Clientes</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Posición</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre del Cliente</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Comprado</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {bestCustomers.length > 0 ? (
                    bestCustomers.map((customer, index) => (
                      <tr key={customer.id} className="hover:bg-gray-50 transition duration-150">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{index + 1}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{customer.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${customer.totalSpent.toFixed(2)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="px-6 py-4 text-center text-sm text-gray-500">No hay datos de clientes.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <div className="flex justify-between items-center mb-8">
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 hover:text-blue-800 transition duration-200 px-4 py-2 rounded-lg border border-blue-200 hover:border-blue-400"
        >
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Volver al Menú
        </button>
        <h1 className="text-4xl font-extrabold text-gray-900 drop-shadow-sm">Gestión de Clientes</h1>

        <button
          onClick={() => {
            if (!canEdit) return; // protección extra
            setShowForm(true);
            setEditingCustomer({ cedula: '', name: '', email: '', phone: '', address: '' });
            setCurrentSubView('list');
            setFilter('');
          }}
          disabled={!canEdit}
          className={`px-6 py-2 rounded-lg transition duration-200 shadow-md hover:shadow-lg flex items-center ${
            !canEdit ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-green-600 text-white hover:bg-green-700'
          }`}
        >
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nuevo Cliente
        </button>
      </div>

      <div className="mb-6 flex justify-center space-x-4">
        <button
          onClick={() => setCurrentSubView('list')}
          className={`px-6 py-2 rounded-lg font-semibold transition duration-200 ${
            currentSubView === 'list' ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
          }`}
        >
          Lista de Clientes
        </button>
        <button
          onClick={() => setCurrentSubView('bestCustomers')}
          className={`px-6 py-2 rounded-lg font-semibold transition duration-200 ${
            currentSubView === 'bestCustomers' ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
          }`}
        >
          Mejores Clientes
        </button>
      </div>

      {showForm ? (
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-2xl mx-auto transform transition-all duration-300 scale-100 opacity-100">
          <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
            {editingCustomer ? 'Editar Cliente' : 'Registrar Nuevo Cliente'}
          </h2>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSaveCustomer(editingCustomer || {});
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cédula</label>
                <input
                  type="text"
                  value={editingCustomer?.cedula || ''}
                  onChange={(e) => setEditingCustomer(prev => ({ ...prev, cedula: e.target.value.replace(/\D/g, '') }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <input
                  type="text"
                  value={editingCustomer?.name || ''}
                  onChange={(e) => setEditingCustomer(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={editingCustomer?.email || ''}
                  onChange={(e) => setEditingCustomer(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                <input
                  type="text"
                  value={editingCustomer?.phone || ''}
                  onChange={(e) => setEditingCustomer(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
                <input
                  type="text"
                  value={editingCustomer?.address || ''}
                  onChange={(e) => setEditingCustomer(prev => ({ ...prev, address: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            <div className="mt-8 flex justify-end space-x-3">
              <button type="button" onClick={() => setShowForm(false)} className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300">
                Cancelar
              </button>
              <button
                type="submit"
                disabled={!canEdit}
                className={`px-6 py-2 rounded-lg ${!canEdit ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
              >
                {editingCustomer ? 'Guardar Cambios' : 'Registrar Cliente'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        renderSubView()
      )}

      {selectedCustomerHistory && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-2xl w-full">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
              Historial de Compras de {selectedCustomerHistory.name}
            </h2>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Filtrar por Fecha</label>
              <div className="flex space-x-4">
                <input
                  type="date"
                  value={historyFilters.startDate}
                  onChange={(e) => setHistoryFilters(prev => ({ ...prev, startDate: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
                <input
                  type="date"
                  value={historyFilters.endDate}
                  onChange={(e) => setHistoryFilters(prev => ({ ...prev, endDate: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            {(selectedCustomerHistory.purchaseHistory || [])
              .filter(p =>
                (!historyFilters.startDate || p.date >= historyFilters.startDate) &&
                (!historyFilters.endDate || p.date <= historyFilters.endDate)
              ).length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID Venta</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Productos</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(selectedCustomerHistory.purchaseHistory || [])
                      .filter(p =>
                        (!historyFilters.startDate || p.date >= historyFilters.startDate) &&
                        (!historyFilters.endDate || p.date <= historyFilters.endDate)
                      )
                      .map(purchase => (
                        <tr key={purchase.saleId} className="hover:bg-gray-50 transition duration-150">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{purchase.saleId}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{purchase.date}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${(purchase.total || 0).toFixed(2)}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            <ul className="list-disc pl-5">
                              {(purchase.products || []).map((prod, idx) => (
                                <li key={idx}>
                                  {prod.name} (x{prod.qty ?? prod.quantity ?? 0})
                                </li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-gray-500">No hay historial de compras para este cliente en el rango seleccionado.</p>
            )}

            <div className="mt-8 flex justify-end">
              <button
                onClick={() => setSelectedCustomerHistory(null)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerManagementView;
